import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.jsx'
import { Link } from 'react-router-dom'
import { RiFolderLine, RiImageLine, RiFlashlightLine, RiUserLine } from 'react-icons/ri'

function StatCard({ label, value, hint, icon, color = 'blue' }) {
  const iconColors = {
    blue:   'bg-blue-100 text-blue-600',
    emerald:'bg-emerald-100 text-emerald-600',
    violet: 'bg-violet-100 text-violet-600',
    amber:  'bg-amber-100 text-amber-600',
  }

  return (
    <div className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-gray-300 hover:shadow transition-all">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconColors[color]}`}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="mt-1.5 text-xs text-gray-400">{hint}</div>}
    </div>
  )
}

export default function DashboardPage() {
  const { user } = useAuth()
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/admin/image-types')
      .then(({ data }) => setTypes(data.data))
      .finally(() => setLoading(false))
  }, [])

  const totalActive = types.reduce((s, t) => s + (t.active_images_count ?? 0), 0)
  const limited = types.filter((t) => !t.is_unlimited)

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Selamat Datang, {user?.name}</h1>
        <p className="mt-1 text-sm text-gray-500">Kelola gambar dan konten yang tampil di website LPK Asa Hikari Mulya.</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Tipe gambar" value={types.length} icon={<RiFolderLine />} color="blue" />
        <StatCard label="Gambar aktif" value={totalActive} icon={<RiImageLine />} color="emerald" />
        <StatCard label="Slot terbatas" value={limited.length} hint={limited.slice(0, 2).map((t) => `${t.slug}: ${t.active_images_count}/${t.max_active}`).join(' · ')} icon={<RiFlashlightLine />} color="violet" />
        <StatCard label="Peran" value={user?.roles?.[0]?.replace('_', ' ') || '—'} icon={<RiUserLine />} color="amber" />
      </div>

      {/* Slot table */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Slot Gambar</h2>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-100">
            {loading && <div className="px-6 py-10 text-center text-sm text-gray-400">Memuat…</div>}
            {!loading && types.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{t.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.slug}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    {t.is_unlimited ? (
                      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                        Unlimited
                      </span>
                    ) : (
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                        t.active_images_count >= t.max_active
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}>
                        {t.active_images_count}/{t.max_active}
                      </span>
                    )}
                  </div>
                  <Link
                    to="/gambar"
                    state={{ typeId: t.id }}
                    className="flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    Kelola →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
